<template>
  <div
    v-tooltip.bottom="toolTipName"
    :class="[
      { 'bg-surface-light text-brand': imagePath },
      sizeClass,
    ]"
    class="am3-avatar aspect-square select-none has-[img]:border-2 has-[img]:border-white has-[img]:text-brand inline-flex items-center justify-center rounded-full bg-surfaces-neutral text-font-disabled overflow-hidden transition-all"
  >
    <img
      v-if="imagePath"
      :src="imagePath"
      :alt="`Avatar for ${name}`"
      class="am3-avatar-image size-full object-cover"
      @error="handleImageError"
    >
    <div
      v-else
      class="am3-avatar-initials font-bold uppercase select-none"
    >
      <span>{{ initials }}</span>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, computed, ref } from 'vue'
import type { PropType } from 'vue'
import '@/components/am3-generic/core/theme.css'

export type AvatarSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl'

export default defineComponent({
  name: 'AM3Avatar',
  props: {
    imagePath: {
      type: String,
      default: null,
    },
    name: {
      type: String,
      default: null,
    },
    size: {
      type: String as PropType<AvatarSize>,
      default: 'md',
      validator: (value: string) => ['xs', 'sm', 'md', 'lg', 'xl'].includes(value),
    },
  },

  setup(props) {
    const hasImageError = ref(false)

    const initials = computed(() => {
      if (!props.name) return ''
      if (props.name === 'null null') return ''

      const names = props.name.trim().split(' ')
      if (names.length === 0) return ''

      const firstInitial = names[0][0] || ''
      const lastInitial = names.length > 1 ? names[names.length - 1][0] : ''

      return (firstInitial + lastInitial).toUpperCase()
    })

    const sizeClass = computed(() => {
      return {
        xs: 'size-6 text-sm',
        sm: 'size-6 md:size-8 md:text-sm',
        md: 'size-6 md:size-10 md:text-md',
        lg: 'size-6 md:size-12 md:text-md',
        xl: 'size-6 md:size-12 lg:size-20 md:text-xl',
      }[props.size]
    })

    const handleImageError = () => {
      hasImageError.value = true
    }

    // Hide the tooltip if the name is "null null"
    const toolTipName = computed(() => {
      return props.name === 'null null' ? null : props.name
    })

    return {
      initials,
      sizeClass,
      handleImageError,
      toolTipName,
    }
  },
})
</script>
